import React from "react";
import "@/App.css";
import { BrowserRouter, Routes, Route, Navigate } from "react-router-dom";

import { AuthProvider, useAuth } from "@/lib/AuthContext";
import LandingPage from "@/pages/LandingPage";
import LoginPage from "@/pages/auth/LoginPage";
import RegisterPage from "@/pages/auth/RegisterPage";
import ForgotPasswordPage from "@/pages/auth/ForgotPasswordPage";
import ResetPasswordPage from "@/pages/auth/ResetPasswordPage";
import DashboardPage from "@/pages/dashboard/DashboardPage";
import VideoGeneratePage from "@/pages/dashboard/VideoGeneratePage";
import ImageGeneratePage from "@/pages/dashboard/ImageGeneratePage";
import VoiceoverGeneratePage from "@/pages/dashboard/VoiceoverGeneratePage";
import RendersPage from "@/pages/dashboard/RendersPage";
import PricingPage from "@/pages/dashboard/PricingPage";
import BillingSuccessPage from "@/pages/dashboard/BillingSuccessPage";
import ApiKeysPage from "@/pages/dashboard/ApiKeysPage";
import OwnerConsolePage from "@/pages/owner/OwnerConsolePage";
import OwnerStudioPage from "@/pages/owner/OwnerStudioPage";
import OwnerRendersPage from "@/pages/owner/OwnerRendersPage";
import OwnerPlatformKeysPage from "@/pages/owner/OwnerPlatformKeysPage";
import UKHealthPage from "@/pages/admin/UKHealthPage";
import CreditsPage from "@/pages/dashboard/CreditsPage";
import TemplatesPage from "@/pages/dashboard/TemplatesPage";
import DirectorPage from "@/pages/dashboard/DirectorPage";
import LogoGeneratorPage from "@/pages/dashboard/LogoGeneratorPage";
import AdGeneratorPage from "@/pages/dashboard/AdGeneratorPage";
import WordsmithPage from "@/pages/dashboard/WordsmithPage";
import CommercialGeneratePage from "@/pages/dashboard/CommercialGeneratePage";
import MagazineGeneratePage from "@/pages/dashboard/MagazineGeneratePage";
import AuthCallback from "@/pages/auth/AuthCallback";

function GoogleHashHandler({ children }) {
  // If the URL fragment contains session_id, intercept and run the AuthCallback
  // BEFORE any other route is rendered. This is the only safe way to catch the
  // Emergent Google return URL.
  if (typeof window !== "undefined" && window.location.hash.includes("session_id=")) {
    return <AuthCallback />;
  }
  return children;
}

function Protected({ children }) {
  const { user, loading } = useAuth();
  if (loading || user === undefined) {
    return <div className="min-h-screen bg-[#080808] flex items-center justify-center text-[#A0A0A0]">Loading…</div>;
  }
  if (!user) return <Navigate to="/login" replace />;
  return children;
}

function App() {
  return (
    <div className="App bg-[#080808] text-[#F4F4F0] min-h-screen">
      <AuthProvider>
        <BrowserRouter>
          <GoogleHashHandler>
            <Routes>
            <Route path="/" element={<LandingPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/register" element={<RegisterPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />

            <Route path="/dashboard" element={<Protected><DashboardPage /></Protected>} />
            <Route path="/generate/video" element={<Protected><VideoGeneratePage /></Protected>} />
            <Route path="/generate/image" element={<Protected><ImageGeneratePage /></Protected>} />
            <Route path="/generate/voiceover" element={<Protected><VoiceoverGeneratePage /></Protected>} />
            <Route path="/renders" element={<Protected><RendersPage /></Protected>} />
            <Route path="/pricing" element={<Protected><PricingPage /></Protected>} />
            <Route path="/billing/success" element={<Protected><BillingSuccessPage /></Protected>} />
            <Route path="/settings/api-keys" element={<Protected><ApiKeysPage /></Protected>} />
            <Route path="/credits" element={<Protected><CreditsPage /></Protected>} />
            <Route path="/templates" element={<Protected><TemplatesPage /></Protected>} />
            <Route path="/director" element={<Protected><DirectorPage /></Protected>} />
            <Route path="/generate/logo" element={<Protected><LogoGeneratorPage /></Protected>} />
            <Route path="/generate/ad" element={<Protected><AdGeneratorPage /></Protected>} />
            <Route path="/wordsmith" element={<Protected><WordsmithPage /></Protected>} />
            <Route path="/generate/commercial" element={<Protected><CommercialGeneratePage /></Protected>} />
            <Route path="/magazine" element={<Protected><MagazineGeneratePage /></Protected>} />
            <Route path="/owner" element={<Protected><OwnerConsolePage /></Protected>} />
            <Route path="/owner/studio" element={<Navigate to="/owner/studio/video" replace />} />
            <Route path="/owner/studio/:mode" element={<Protected><OwnerStudioPage /></Protected>} />
            <Route path="/owner/renders" element={<Protected><OwnerRendersPage /></Protected>} />
            <Route path="/owner/platform-keys" element={<Protected><OwnerPlatformKeysPage /></Protected>} />
            <Route path="/admin" element={<Protected><OwnerConsolePage /></Protected>} />
            <Route path="/admin/studio" element={<Navigate to="/owner/studio/video" replace />} />
            <Route path="/admin/studio/:mode" element={<Protected><OwnerStudioPage /></Protected>} />
            <Route path="/admin/renders" element={<Protected><OwnerRendersPage /></Protected>} />
            <Route path="/admin/platform-keys" element={<Protected><OwnerPlatformKeysPage /></Protected>} />
            <Route path="/admin/uk-health" element={<Protected><UKHealthPage /></Protected>} />
            <Route path="/owner/uk-health" element={<Protected><UKHealthPage /></Protected>} />

            <Route path="*" element={<Navigate to="/" replace />} />
            </Routes>
          </GoogleHashHandler>
        </BrowserRouter>
      </AuthProvider>
    </div>
  );
}

export default App;
